/**
 * Zapytania modułu `cases` (TanStack Query v5).
 *
 * Moduły `documents` i `deadlines` powstają PO tym module i mogą być wyłączone
 * (`MODULES_ENABLED`), więc każde odwołanie do nich idzie przez `optionalGet` —
 * 404/403/brak połączenia dają `null`, a UI pokazuje „—" albo stan pusty.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Query } from '@/lib/api'
import { ApiError, api } from '@/lib/api'
import type { Paginated } from '@/lib/types'
import type {
  CaseCreatePayload,
  CaseDetail,
  CaseEvent,
  CaseFilters,
  CaseRow,
  CaseStats,
  DeadlineLike,
  DocumentLike,
  Lawyer,
} from './types'

export const casesKeys = {
  all: ['cases'] as const,
  list: (params: Query) => ['cases', 'list', params] as const,
  detail: (id: string | number) => ['cases', 'detail', String(id)] as const,
  events: (id: string | number) => ['cases', 'events', String(id)] as const,
  stats: ['cases', 'stats'] as const,
  filters: ['cases', 'filters'] as const,
  lawyers: ['cases', 'lawyers'] as const,
  my: (limit: number) => ['cases', 'my', limit] as const,
  deadlines: (id: string | number) => ['cases', 'deadlines', String(id)] as const,
  upcoming: (ids: number[]) => ['cases', 'deadlines', 'upcoming', ids.join(',')] as const,
  documents: (id: string | number) => ['cases', 'documents', String(id)] as const,
  time: (id: string | number) => ['cases', 'time', String(id)] as const,
  tasks: (id: string | number) => ['cases', 'tasks', String(id)] as const,
}

/**
 * Kształt wpisu czasu z modułu `timesheet` — moduł bywa wyłączony, więc typ jest
 * celowo tolerancyjny (jak `DeadlineLike`); trzymamy go tutaj, bo to typ „na styku",
 * a nie kontrakt modułu cases.
 */
export interface TimeEntryLike {
  id: number | string
  case_id?: number | null
  work_date?: string | null
  minutes?: number | null
  description?: string | null
  billable?: boolean | null
  user?: { id: number; name?: string | null; short_name?: string | null } | null
}

/** Odpowiedź listy timesheetu: paginacja + suma minut całego przefiltrowanego zbioru. */
export interface TimeEntriesLike extends Paginated<TimeEntryLike> {
  total_minutes?: number
}

/**
 * Kształt zadania z modułu `tasks` — moduł bywa wyłączony, więc typ jest celowo
 * tolerancyjny (jak `TimeEntryLike`); trzymamy go tutaj, bo to typ „na styku",
 * a nie kontrakt modułu cases.
 */
export interface TaskLike {
  id: number | string
  case_id?: number | null
  title?: string | null
  description?: string | null
  due_date?: string | null
  status?: string | null
  assignee?: { id: number; name?: string | null; short_name?: string | null } | null
}

/**
 * GET, który nie wywraca ekranu, gdy moduł docelowy nie istnieje / jest wyłączony.
 *
 * `null` zwracamy WYŁĄCZNIE dla 404 (brak routera modułu) i 403 (rola bez uprawnienia) —
 * to sytuacje, w których „—" jest poprawną odpowiedzią. Każdy inny błąd (401, 5xx, brak
 * połączenia) musi polecieć dalej: wcześniej padająca sesja albo awaria backendu wyglądały
 * na ekranie identycznie jak „brak terminów", a użytkownik nie dostawał żadnego sygnału.
 */
export async function optionalGet<T>(path: string, query?: Query): Promise<T | null> {
  try {
    return await api.get<T>(path, query ? { query } : undefined)
  } catch (error) {
    if (error instanceof ApiError && (error.isNotFound || error.isForbidden)) return null
    throw error
  }
}

/** Backend może zwrócić listę albo `Paginated<T>` — bierzemy `items`, gdy są. */
export function toItems<T>(payload: Paginated<T> | T[] | null | undefined): T[] {
  if (!payload) return []
  if (Array.isArray(payload)) return payload
  return Array.isArray(payload.items) ? payload.items : []
}

/* ------------------------------------------------------------- sprawy */

export interface CasesListParams {
  page: number
  size?: number
  status?: string
  client?: string
  lawyer_id?: number | ''
  court?: string
  kind?: string
  q?: string
  sort?: string
  direction?: 'asc' | 'desc'
}

export function useCasesQuery(params: CasesListParams) {
  const query: Query = { ...params }
  return useQuery({
    queryKey: casesKeys.list(query),
    queryFn: () => api.get<Paginated<CaseRow>>('/cases', { query }),
    placeholderData: (previous) => previous,
  })
}

export function useCaseQuery(caseId: string | undefined) {
  return useQuery({
    queryKey: casesKeys.detail(caseId ?? ''),
    queryFn: () => api.get<CaseDetail>(`/cases/${caseId}`),
    enabled: Boolean(caseId),
    retry: false,
  })
}

export function useCaseEvents(caseId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: casesKeys.events(caseId ?? ''),
    queryFn: () => api.get<Paginated<CaseEvent>>(`/cases/${caseId}/events`, { query: { size: 200 } }),
    enabled: Boolean(caseId) && enabled,
  })
}

export function useCaseStats() {
  return useQuery({
    queryKey: casesKeys.stats,
    queryFn: () => api.get<CaseStats>('/cases/stats'),
    staleTime: 60_000,
  })
}

export function useCaseFilters() {
  return useQuery({
    queryKey: casesKeys.filters,
    queryFn: () => api.get<CaseFilters>('/cases/filters'),
    staleTime: 5 * 60_000,
  })
}

export function useLawyers(enabled = true) {
  return useQuery({
    queryKey: casesKeys.lawyers,
    queryFn: () => api.get<Lawyer[]>('/cases/lawyers'),
    staleTime: 5 * 60_000,
    enabled,
  })
}

export function useMyCases(limit = 5) {
  return useQuery({
    queryKey: casesKeys.my(limit),
    queryFn: () => api.get<CaseRow[]>('/cases/my', { query: { limit } }),
    staleTime: 60_000,
  })
}

export function useCreateCase() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CaseCreatePayload) => api.post<CaseDetail>('/cases', payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: casesKeys.all })
    },
  })
}

/* ----------------------------------------------- moduły opcjonalne */

/** Terminy sprawy — `GET /api/deadlines?case_id=`; `null` gdy modułu nie ma. */
export function useCaseDeadlines(caseId: string | undefined) {
  return useQuery({
    queryKey: casesKeys.deadlines(caseId ?? ''),
    queryFn: () => optionalGet<Paginated<DeadlineLike> | DeadlineLike[]>('/deadlines', { case_id: caseId, size: 50 }),
    enabled: Boolean(caseId),
    retry: false,
    staleTime: 60_000,
  })
}

/**
 * Najbliższe terminy dla widocznych spraw — kolumna NAJBLIŻSZY TERMIN w rejestrze.
 * `GET /api/deadlines/upcoming?case_ids=1&case_ids=2…`
 *
 * Endpoint zwraca najbliższy NIEZAMKNIĘTY termin sprawy — także taki po upływie
 * (zaległy). Dzięki temu kolumna rejestru i chip na karcie sprawy mówią o tej samej
 * sprawie to samo; wcześniej terminy przeterminowane były odfiltrowane i rejestr
 * pisał „brak terminów" przy sprawie z czerwonym chipem „termin 11 dni temu".
 */
export function useUpcomingDeadlines(caseIds: number[]) {
  const ids = [...caseIds].sort((a, b) => a - b)
  return useQuery({
    queryKey: casesKeys.upcoming(ids),
    queryFn: () =>
      optionalGet<Paginated<DeadlineLike> | DeadlineLike[]>('/deadlines/upcoming', {
        case_ids: ids,
        size: 200,
      }),
    enabled: ids.length > 0,
    retry: false,
    staleTime: 60_000,
  })
}

/** Wpisy czasu sprawy — `GET /api/timesheet?case_id=`; `null` gdy modułu nie ma (403/404). */
export function useCaseTimeEntries(caseId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: casesKeys.time(caseId ?? ''),
    queryFn: () => optionalGet<TimeEntriesLike>('/timesheet', { case_id: caseId, size: 50 }),
    enabled: Boolean(caseId) && enabled,
    retry: false,
    staleTime: 60_000,
  })
}

/** Zadania sprawy — `GET /api/tasks?case_id=`; `null` gdy modułu nie ma (403/404). */
export function useCaseTasks(caseId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: casesKeys.tasks(caseId ?? ''),
    queryFn: () => optionalGet<Paginated<TaskLike> | TaskLike[]>('/tasks', { case_id: caseId, size: 50 }),
    enabled: Boolean(caseId) && enabled,
    retry: false,
    staleTime: 60_000,
  })
}

/** Dokumenty sprawy — `GET /api/documents?case_id=`; `null` gdy modułu nie ma. */
export function useCaseDocuments(caseId: string | undefined) {
  return useQuery({
    queryKey: casesKeys.documents(caseId ?? ''),
    queryFn: () => optionalGet<Paginated<DocumentLike> | DocumentLike[]>('/documents', { case_id: caseId, size: 100 }),
    enabled: Boolean(caseId),
    retry: false,
    staleTime: 60_000,
  })
}

/* -------------------------------------------------------- normalizacja */

export function deadlineDate(deadline: DeadlineLike): string | null {
  return deadline.due_date ?? deadline.date ?? null
}

export function deadlineTitle(deadline: DeadlineLike): string {
  return deadline.title ?? deadline.name ?? deadline.kind ?? 'Termin'
}

export function documentTitle(doc: DocumentLike): string {
  return doc.title ?? doc.file_name ?? `Dokument #${doc.id}`
}

export function documentDate(doc: DocumentLike): string | null {
  return doc.document_date ?? doc.received_at ?? doc.created_at ?? null
}
